/**
 * Chart configuration and theme colors matching shadcn/ui design system.
 * Used by Recharts components throughout the dashboard.
 */

/**
 * Chart color palette matching shadcn/ui theme variables.
 * Uses HSL format for easy theme integration.
 */
export const chartColors = {
  // Primary colors for main data series — violet brand
  primary: 'hsl(var(--primary))',
  primaryForeground: 'hsl(var(--primary-foreground))',
  
  // Status colors for run states — refined to match Ignix67 palette
  success: 'hsl(152, 60%, 40%)',    // Teal-shifted green (matches --status-succeeded)
  warning: 'hsl(25, 90%, 53%)',     // Orange (matches --status-cancelled)
  error: 'hsl(0, 72%, 51%)',        // Red (matches --status-failed)
  info: 'hsl(258, 58%, 54%)',       // Violet — brand-aligned running state
  muted: 'hsl(var(--muted-foreground))', // Gray for cancelled/unknown
  
  // Token usage chart colors — violet-tinted
  tokenInput: 'hsl(258, 30%, 50%)',   // Muted violet for input (large volume)
  tokenOutput: 'hsl(180, 50%, 45%)',  // Teal for output (smaller volume)
  
  // Chart-specific colors for multi-series data — violet-first palette
  series: [
    'hsl(258, 58%, 54%)',  // Violet (brand primary)
    'hsl(152, 60%, 40%)',  // Teal green
    'hsl(217, 80%, 56%)',  // Blue  
    'hsl(25, 90%, 53%)',   // Orange
    'hsl(0, 72%, 51%)',    // Red
    'hsl(180, 60%, 42%)',  // Cyan
  ],
  
  // Background and grid colors
  background: 'hsl(var(--background))',
  foreground: 'hsl(var(--foreground))',
  border: 'hsl(var(--border))',
  grid: 'hsl(var(--border))',
} as const

/**
 * Run status color mapping for consistent visualization.
 */
export const statusColors: Record<string, string> = {
  Completed: chartColors.success,
  Running: chartColors.info,
  Pending: chartColors.warning,
  Failed: chartColors.error,
  Cancelled: chartColors.muted,
  Unknown: chartColors.muted,
}

/**
 * Default chart dimensions and spacing.
 */
export const chartDefaults = {
  /** Standard chart height for cards */
  height: 300,
  /** Compact chart height for smaller displays */
  compactHeight: 200,
  /** Margin for charts with axes */
  margin: { top: 10, right: 10, bottom: 20, left: 40 },
  /** Margin for pie/donut charts */
  pieMargin: { top: 10, right: 10, bottom: 10, left: 10 },
  /** Animation duration in ms */
  animationDuration: 300,
} as const

/**
 * Tooltip styles matching shadcn/ui design.
 */
export const tooltipStyles = {
  contentStyle: {
    backgroundColor: 'hsl(var(--popover))',
    border: '1px solid hsl(var(--border))',
    borderRadius: '6px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    padding: '8px 12px',
    fontSize: '12px',
  },
  labelStyle: {
    color: 'hsl(var(--foreground))',
    fontWeight: 600,
    marginBottom: '4px',
  },
  itemStyle: {
    color: 'hsl(var(--muted-foreground))',
    padding: '2px 0',
  },
} as const

/**
 * Format a number for chart display (e.g., 1000 -> 1K).
 */
export function formatChartNumber(value: number): string {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`
  }
  return value.toString()
}

/**
 * Format a date for chart axis labels.
 */
export function formatChartDate(dateStr: string, format: 'short' | 'medium' = 'short'): string {
  const date = new Date(dateStr)
  if (format === 'short') {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
}

/**
 * Format percentage for chart tooltips.
 */
export function formatPercentage(value: number, total: number): string {
  if (total === 0) return '0%'
  return `${((value / total) * 100).toFixed(1)}%`
}
